import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';

import type {
  PoProjectInvoiceRequestDto,
  RegenerateInvoiceRequestDto,
  RegenerateInvoiceResponseDto,
  SurveyFinanceDownloadRequestDto,
  SurveyFinanceReportResponseDto,
  SurveyFinanceXlsxRequestDto,
} from './dtos/invoice-download.dto';

@Injectable({ providedIn: 'root' })
export class InvoiceDownloadApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._env.pureSpectrum.url;

  public surveyFinanceReport(
    companyId: number,
    companyName: string,
    period: string,
  ): Observable<SurveyFinanceReportResponseDto> {
    return this._http.get<SurveyFinanceReportResponseDto>(
      `${this._baseUrl}/survey/finance`,
      { params: { company_id: companyId, company_name: companyName, period } },
    );
  }

  public surveyFinanceFileUrl(fileName: string): string {
    return `${this._baseUrl}/survey/download-invoice/${fileName}`;
  }

  public poProjectInvoice(dto: PoProjectInvoiceRequestDto): Observable<Blob> {
    return this._http.post(`${this._baseUrl}/invoice/download`, dto, {
      responseType: 'blob',
    });
  }

  public surveyFinance(dto: SurveyFinanceDownloadRequestDto): Observable<Blob> {
    return this._http.post(this._financeEndpoint(dto.period), dto, {
      responseType: 'blob',
    });
  }

  public surveyFinanceXlsx(dto: SurveyFinanceXlsxRequestDto): Observable<Blob> {
    return this._http.post(this._financeEndpoint(dto.period), dto, {
      responseType: 'blob',
    });
  }

  public rewardCsv(
    companyId: number,
    month: string,
    year: number,
  ): Observable<HttpResponse<Blob>> {
    const params: Record<string, string | number> = { month, year };
    if (companyId !== 0) {
      params['supId'] = companyId;
    }
    return this._http.get(`${this._baseUrl}/micro-rewards/v1/report`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  public regenerateInvoice(
    dto: RegenerateInvoiceRequestDto,
  ): Observable<RegenerateInvoiceResponseDto> {
    return this._http.post<RegenerateInvoiceResponseDto>(
      `${this._baseUrl}/invoice/regenerate`,
      dto,
    );
  }

  private _financeEndpoint(period: string): string {
    return parseInt(period, 10) < 201708
      ? `${this._baseUrl}/survey/pdfinvoice/finance/V1`
      : `${this._baseUrl}/survey/pdfinvoice/finance`;
  }
}
